<script setup lang="ts">
import { ref, onMounted } from 'vue'

import { Delete } from '@element-plus/icons-vue'
import { Icon } from '@iconify/vue'
import { ElMessage } from 'element-plus'
import { storeToRefs } from 'pinia'

import LoadingSpinner from '@/components/UI/LoadingSpinner.vue'

import { useConfirmDialog } from '@/composables/useConfirmDialog'
import { triggerListRefresh } from '@/composables/useListRefresh'

import { checkFileMagicNumbers } from '@/utils/file/checkImageType'
import { notifySuccess } from '@/utils/ui/handleNotification'

import { feUserProfileAPI } from '@/APIs'
import { AvatarSize, ProfileSteps } from '@/constant/user'
import appStore from '@/stores'

const { updateCurrentStep } = appStore.useFEUserProfileStore
const { userPhoneNumber } = storeToRefs(appStore.useFEUserProfileStore)
const { userInfo, userHDAvatarUrl } = storeToRefs(appStore.useAuthStore)
const { getUserInfo, setUserBlobAvatar } = appStore.useAuthStore

const password = ref('myPassword')

const previewBase64Url = ref('')
const avatarChanged = ref(false)
const loginType = ref('')

const initialUserName = ref('')
const displayUserName = ref('')
const showUpdateBtn = ref(false)
const isCustomAvatar = ref(false)

// 控制確定按鈕-Loading狀態
const isLoading = ref(false)

const refreshUserInfo = async () => {
  await getUserInfo()
  await Promise.allSettled([setUserBlobAvatar(AvatarSize.SMALL), setUserBlobAvatar(AvatarSize.LARGE)])
  isAvatarLoading.value = false
}

const checkIsCustomAvatar = () => {
  if (userInfo.value) {
    isCustomAvatar.value = userInfo.value.is_custom_avatar
  }
}

onMounted(async () => {
  if (userInfo.value) {
    displayUserName.value = userInfo.value.name
    initialUserName.value = userInfo.value.name
    loginType.value = userInfo.value.provider
  }
  checkIsCustomAvatar()
  await setUserBlobAvatar(AvatarSize.LARGE)
})

const checkChanges = () => {
  showUpdateBtn.value = initialUserName.value !== displayUserName.value || avatarChanged.value
}

const userNameInputRef = ref<null | HTMLInputElement>(null)
const userNameInputError = ref(false)

// 更新使用者名稱
const handleUpdateUserName = async () => {
  if (displayUserName.value === '') {
    userNameInputError.value = true
    return false
  }
  userNameInputError.value = false
  isLoading.value = true
  try {
    await feUserProfileAPI.updateUserName(displayUserName.value)
    await getUserInfo()
    initialUserName.value = displayUserName.value
    userNameInputRef.value?.blur()
    return true
  } catch (error: any) {
    const errorMsg = error.response.data.message ? error.response.data.message : error.message
    console.error(errorMsg)
  } finally {
    isLoading.value = false
  }
  return false
}

// 控制變更頭像-Loading狀態
const isAvatarLoading = ref(false)

// 更新個人頭像
const handleUpdateAvatar = async () => {
  if (avatarChanged.value && avatarFile.value) {
    isLoading.value = true
    isAvatarLoading.value = true
    previewBase64Url.value = ''
    try {
      await feUserProfileAPI.updateUserAvatar(avatarFile.value)
      await refreshUserInfo()
      avatarChanged.value = false
      return true
    } catch (error: any) {
      isAvatarLoading.value = false
      const errorMsg = error.response.data.message ? error.response.data.message : error.message
      console.error(errorMsg)
    } finally {
      isLoading.value = false
    }
  }
  return false
}

// 移除使用者上傳頭像
const handleRemoveAvatar = async () => {
  isLoading.value = true
  isAvatarLoading.value = true
  try {
    await feUserProfileAPI.removeUserAvatar()
    await refreshUserInfo()
    checkIsCustomAvatar()
    notifySuccess('頭像移除成功')
    triggerListRefresh()
  } catch (error: any) {
    isAvatarLoading.value = false
    const errorMsg = error.response.data.message ? error.response.data.message : error.message
    console.error(errorMsg)
  } finally {
    isLoading.value = false
  }
}

/** 開啟移除使用者上傳頭像彈窗 */
const openConfirmRemoveAvatarDialog = () => {
  if (previewBase64Url.value) {
    previewBase64Url.value = ''
    avatarChanged.value = false
    checkChanges()
    return
  }
  const { confirm } = useConfirmDialog()
  confirm({
    title: '是否移除頭像',
    text: '確定要移除上傳的頭像嗎？',
    confirmButtonText: '確定',
    onConfirm: async () => {
      await handleRemoveAvatar()
    }
  })
}

const fileInput = ref<null | HTMLInputElement>(null)
const triggerFileInput = () => {
  fileInput.value?.click()
}

const avatarFile = ref<File | null>(null)

// 本地預覽頭像
const previewAvatar = async (event: Event) => {
  const input = event.target as HTMLInputElement
  const file = input.files ? input.files[0] : null
  const maxFileSize = 1048576 // 1MB
  if (!file) {
    return
  }

  if (file.size > maxFileSize) {
    ElMessage.error('檔案大小不能超過1MB')
    return
  }
  avatarFile.value = file
  isCustomAvatar.value = true
  try {
    const isValidType = await checkFileMagicNumbers(file)
    if (isValidType) {
      const dataUrlReader = new FileReader()
      dataUrlReader.onload = () => {
        previewBase64Url.value = dataUrlReader.result as string
        avatarChanged.value = true
        checkChanges()
      }
      dataUrlReader.readAsDataURL(file)
    } else {
      ElMessage.error('只允許上傳 JPG, JPEG, PNG 格式的圖片。')
    }
  } catch (error) {
    console.error(error)
    ElMessage.error('檔案上傳過程中發生錯誤')
  }
}

const handleUpdate = async () => {
  let userNameUpdated = false
  let avatarUpdated = false

  // 檢查是否需要更新使用者名稱
  if (initialUserName.value !== displayUserName.value) {
    userNameUpdated = await handleUpdateUserName()
  }

  // 檢查是否需要更新頭像
  if (avatarChanged.value && avatarFile.value) {
    avatarUpdated = await handleUpdateAvatar()
  }

  if (userNameUpdated && avatarUpdated) {
    notifySuccess('名稱和頭像更新成功')
  } else if (userNameUpdated) {
    notifySuccess('名稱更新成功')
  } else if (avatarUpdated) {
    notifySuccess('頭像更新成功')
  }

  if (userNameUpdated || avatarUpdated) {
    showUpdateBtn.value = false
    triggerListRefresh()
  }
}
</script>
<template>
  <div class="profile-info">
    <div class="profile-avatar">
      <template v-if="userHDAvatarUrl && !isAvatarLoading">
        <img v-if="previewBase64Url" :src="previewBase64Url" alt="avatar" class="profile-avatar-image" />
        <img v-else :src="userHDAvatarUrl" alt="avatar" class="profile-avatar-image" />
        <span
          class="absolute -bottom-0.5 -right-4 z-50 flex h-7 w-7 cursor-pointer items-center justify-center rounded-full bg-app-light-blue-400"
          @click="triggerFileInput"
        >
          <Icon icon="ri:edit-box-line" width="14px" height="14px" class="text-white" />
        </span>
        <input ref="fileInput" type="file" style="display: none" @change="previewAvatar" />
        <div v-if="isCustomAvatar" class="profile-avatar-overlay">
          <div class="profile-avatar-overlay-shadow"></div>
          <span class="profile-avatar-delete-icon" @click="openConfirmRemoveAvatarDialog">
            <el-icon size="14px" color="#fff"><Delete /></el-icon>
          </span>
        </div>
      </template>
      <div v-else class="profile-avatar-loading">
        <LoadingSpinner />
      </div>
    </div>
    <div class="profile-info-common">
      <p class="profile-step-title">一般</p>
      <div v-if="userInfo?.name" class="profile-info-common-item">
        <label class="profile-info-label">個人名稱</label>
        <div>
          <el-input
            ref="userNameInputRef"
            v-model="displayUserName"
            type="text"
            @input="checkChanges"
            @keypress.enter="handleUpdate"
          />
        </div>
        <span v-if="userNameInputError" class="profile-info-error-msg">請輸入正確名稱</span>
      </div>
      <div v-if="userInfo?.email" class="profile-info-common-item">
        <label class="profile-info-label">驗證信箱</label>
        <div class="profile-info-input-wrapper">
          <el-input v-model="userInfo.email" type="email" disabled />
          <button
            type="button"
            class="profile-info-update-btn text-app-brand-blue-500"
            @click="updateCurrentStep(ProfileSteps.UPDATE_EMAIL_STEP_1)"
          >
            變更驗證信箱
          </button>
        </div>
      </div>
      <div>
        <label class="profile-info-label">手機</label>
        <div class="profile-info-input-wrapper">
          <el-input v-model="userPhoneNumber" type="text" disabled />
          <button
            type="button"
            class="profile-info-update-btn text-app-brand-blue-500"
            @click="updateCurrentStep(ProfileSteps.UPDATE_PHONE)"
          >
            變更手機門號
          </button>
        </div>
      </div>
    </div>
    <div v-if="loginType === 'system_account'" class="profile-info-security">
      <p class="profile-step-title">安全性</p>
      <div>
        <div>
          <label class="profile-info-label">密碼</label>
          <div class="profile-info-input-wrapper">
            <el-input v-model="password" type="password" disabled />
            <button
              type="button"
              class="profile-info-update-btn text-app-brand-blue-500"
              @click="updateCurrentStep(ProfileSteps.UPDATE_PASSWORD)"
            >
              變更密碼
            </button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showUpdateBtn" class="profile-steps-btn-wrapper">
      <BaseButton
        :loading="isLoading"
        class="w-[120px] border-app-brand-blue-500 bg-app-brand-blue-500 text-white"
        @click="handleUpdate"
      >
        <span v-if="isLoading">儲存中</span>
        <span v-else>儲存變更</span>
      </BaseButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.profile-avatar {
  margin-top: 20px;
  margin-bottom: 29px;
  display: inline-block;
  position: relative;
  width: 72px;
  height: 72px;

  &-image {
    width: 72px;
    height: 72px;
    border-radius: 8px;
    vertical-align: middle;
    object-fit: cover;
  }
  &-edit-icon {
    cursor: pointer;
    background-color: #000;
    border-radius: 50%;
    width: 28px;
    height: 28px;
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    bottom: -2px;
    right: -8px;
    z-index: 99;
  }
  &-delete-icon {
    padding-top: 2px;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: none;
    transition: opacity 0.3s ease;
    cursor: pointer;
    z-index: 100;
  }
  &-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 8px;
    &-shadow {
      background-color: rgba(0, 0, 0, 0.5);
      width: 72px;
      height: 72px;
      border-radius: 8px;
      transition: opacity 0.3s ease;
      display: none;
    }
  }
  &-loading {
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }
}
.profile-avatar:hover {
  .profile-avatar-overlay-shadow {
    display: block;
  }
  .profile-avatar-delete-icon {
    display: block;
  }
}

.profile-info-common {
  margin-bottom: 32px;
}

.profile-info-input-wrapper {
  display: flex;
  align-items: center;
  gap: 5px;
}
.profile-info-update-btn {
  padding: 6px 18px;
  white-space: nowrap;
  background-color: #fff;
  font-size: 12px;
  border: none;
  cursor: pointer;
}

.profile-info-save-username-btn {
  width: 180px;
  white-space: nowrap;
  padding: 8px 76px;
  cursor: pointer;
  font-weight: 500;
  border: none;
  border-radius: 4px;
  letter-spacing: 0.14px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: var(--primary-3);
  color: #fff;
}

.profile-info-error-msg {
  display: block;
  color: #f56c6c;
  font-size: 12px;
  padding-top: 2px;
}
</style>
